import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { setToken, getToken } from "../utils/token";
import { useNavigate } from "react-router-dom"; // Explicit import
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Fade,
  Backdrop,
} from "@mui/material";

const BASE_URL = "https://pixadb.com/api/v1";

export default function SessionExpiredModal() {
  const [open, setOpen] = useState(false);
  const hasExpired = useRef(false);
  const navigate = useNavigate(); // Correct hook usage

  // Idle tracking
  useEffect(() => {
    const idleLimit = 5 * 60 * 1000;

    const checkIdle = () => {
      const token = getToken();
      if (!token) return;

      const lastActivity = parseInt(localStorage.getItem("last_activity") || "0");
      if (!hasExpired.current && Date.now() - lastActivity > idleLimit) {
        setOpen(true);
        hasExpired.current = true;
      }
    };

    const interval = setInterval(checkIdle, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const updateActivity = () => {
      if (getToken()) {
        localStorage.setItem("last_activity", Date.now().toString());
        hasExpired.current = false;
      }
    };
    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown", updateActivity);
    return () => {
      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
    };
  }, []);

  const handleContinue = async () => {
    try {
      const token = getToken();
      if (!token) return handleLogout();

      const res = await axios.post(
        `${BASE_URL}/refresh-token`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("Refresh successful", res.data);
      setToken(res.data.access_token);
      setOpen(false);
      localStorage.setItem("last_activity", Date.now().toString());
      hasExpired.current = false;
    } catch (err) {
      console.error("Refresh failed", err.response || err);
      handleLogout();
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    setOpen(false);
    navigate("/login"); // Use navigate instead of window.location.href to avoid hard reloads
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 300 }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff8e1", // light golden background
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            p: 5,
            width: "90%",
            maxWidth: 420,
            textAlign: "center",
            fontFamily: "Arial, sans-serif",
          }}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            gutterBottom
            sx={{ color: "#b58800" }} // theme gold color
          >
            Session Expired
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            mb={4}
            sx={{ color: "#5f5f5f" }}
          >
            Your session has expired. Do you want to continue your session?
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              onClick={handleLogout}
              variant="outlined"
              sx={{
                borderColor: "#d32f2f",
                color: "#d32f2f",
                fontWeight: "bold",
                px: 3,
                "&:hover": { borderColor: "#b71c1c", backgroundColor: "#fceaea" },
              }}
            >
              Logout
            </Button>
            <Button
              onClick={handleContinue}
              variant="contained"
              sx={{
                backgroundColor: "#b58800",
                color: "#fff",
                fontWeight: "bold",
                px: 3,
                "&:hover": { backgroundColor: "#9c7000" },
              }}
            >
              Continue
            </Button>
          </Stack>
        </Box>
      </Fade>
    </Modal>
  );
}